import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  MessageSquare,
  Send,
  ShieldCheck,
  BriefcaseBusiness,
  HelpCircle,
  Lightbulb,
  ArrowRight,
  Mail,
} from "lucide-react";
import Swal from "sweetalert2";
import api from "../Api/Api";

export default function Contact() {
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);

  const user_id = localStorage.getItem("user_id");

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!user_id) {
      Swal.fire({
        icon: "warning",
        title: "Sign In Required",
        text: "Please sign in before sending us a message.",
        confirmButtonColor: "#2563EB",
      });

      return;
    }

    if (!message.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Message Required",
        text: "Please write a message before submitting.",
        confirmButtonColor: "#2563EB",
      });

      return;
    }

    if (message.trim().length < 10) {
      Swal.fire({
        icon: "warning",
        title: "Message Too Short",
        text: "Please provide a little more detail so we can assist you better.",
        confirmButtonColor: "#2563EB",
      });

      return;
    }

    setIsSending(true);

    try {
      const response = await api.post("/send_message/", {
        message: message.trim(),
        user_id,
      });

      if (
        response.status === 200 ||
        response.status === 201
      ) {
        await Swal.fire({
          icon: "success",
          title: "Message Sent",
          text: "Your message has been sent successfully. Our team will get back to you as soon as possible.",
          confirmButtonColor: "#2563EB",
        });

        setMessage("");
      }
    } catch (error) {
      console.error("Error sending message:", error);

      Swal.fire({
        icon: "error",
        title: "Message Not Sent",
        text:
          error.response?.data?.message ||
          error.response?.data?.detail ||
          "There was an error sending your message. Please try again.",
        confirmButtonColor: "#2563EB",
      });
    } finally {
      setIsSending(false);
    }
  };

  const contactReasons = [
    {
      icon: BriefcaseBusiness,
      title: "Start a Project",
      description:
        "Tell us about the website, mobile app, business system, or digital product you want to build.",
    },
    {
      icon: Lightbulb,
      title: "Technology Consultation",
      description:
        "Need guidance on a software idea or technical challenge? We can help you plan the right approach.",
    },
    {
      icon: HelpCircle,
      title: "General Inquiry",
      description:
        "Have a question about Nexindi Tech, our services, products, or partnerships? Send us a message.",
    },
  ];

  return (
    <div className="min-h-screen bg-white text-slate-900 overflow-hidden">
      {/* Hero */}
      <section className="relative bg-slate-950 text-white pt-28 pb-24 px-6 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-[150px]" />

        <div className="absolute -bottom-40 right-0 w-[500px] h-[500px] bg-cyan-400/10 rounded-full blur-[140px]" />

        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)
            `,
            backgroundSize: "50px 50px",
          }}
        />

        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-blue-400/20 bg-blue-500/10 text-blue-300 text-sm font-semibold mb-7">
            <MessageSquare size={15} />
            Contact Nexindi Tech
          </div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-tight mb-7">
            Have an idea or challenge?
            <span className="block text-blue-400">
              Let's talk about it.
            </span>
          </h1>

          <p className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-3xl mx-auto">
            Whether you need a new digital product, technical guidance, or
            simply want to learn more about what we do, our team is ready to
            hear from you.
          </p>
        </div>
      </section>

      {/* Contact Reasons */}
      <section className="relative py-20 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            {contactReasons.map((item, index) => {
              const Icon = item.icon;

              return (
                <div
                  key={index}
                  className="bg-white border border-slate-200 rounded-3xl p-7 transition-all duration-500 hover:-translate-y-2 hover:border-blue-200 hover:shadow-xl hover:shadow-blue-900/5"
                >
                  <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-6">
                    <Icon size={22} />
                  </div>

                  <h2 className="text-xl font-bold text-slate-900 mb-3">
                    {item.title}
                  </h2>

                  <p className="text-slate-600 text-sm leading-relaxed">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="relative py-24 px-6 bg-white overflow-hidden">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-blue-100/60 rounded-full blur-[120px]" />

        <div className="relative z-10 max-w-6xl mx-auto grid lg:grid-cols-[0.8fr_1.2fr] gap-12 lg:gap-16 items-start">
          {/* Left Content */}
          <div className="lg:sticky lg:top-28">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 text-blue-600 text-sm font-semibold mb-6">
              <span className="w-2 h-2 rounded-full bg-blue-600" />
              Send Us a Message
            </div>

            <h2 className="text-4xl md:text-5xl font-bold tracking-tight leading-tight mb-6">
              Let's build something
              <span className="text-blue-600"> meaningful.</span>
            </h2>

            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              Share a few details about what you need and we'll review your
              message and respond with the most relevant next steps.
            </p>

            <div className="space-y-4">
              {[
                "Tell us about your idea or challenge",
                "Include important goals or requirements",
                "Our team will review your request",
              ].map((item, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3"
                >
                  <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                    <ShieldCheck size={15} />
                  </div>

                  <span className="text-slate-600">
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Form Card */}
          <div className="bg-white border border-slate-200 rounded-3xl shadow-2xl shadow-slate-900/5 overflow-hidden">
            <div className="px-7 md:px-9 py-7 border-b border-slate-200">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center">
                  <Mail size={21} />
                </div>

                <div>
                  <h3 className="text-xl font-bold text-slate-900">
                    Contact our team
                  </h3>

                  <p className="text-sm text-slate-500 mt-1">
                    Write your inquiry below.
                  </p>
                </div>
              </div>
            </div>

            <form
              onSubmit={handleSendMessage}
              className="p-7 md:p-9"
            >
              <div>
                <div className="flex items-center justify-between gap-4 mb-2">
                  <label
                    htmlFor="message"
                    className="text-sm font-semibold text-slate-700"
                  >
                    Your Message
                  </label>

                  <span className="text-xs text-slate-400">
                    {message.length}/2000
                  </span>
                </div>

                <textarea
                  id="message"
                  placeholder="Tell us about your project, question, or what you'd like help with..."
                  rows={9}
                  maxLength={2000}
                  required
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full px-5 py-4 border border-slate-200 rounded-2xl resize-y outline-none text-slate-700 leading-relaxed transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                />

                <p className="text-xs text-slate-400 mt-2">
                  The more context you provide, the better we can understand
                  your request.
                </p>
              </div>

              {!user_id && (
                <div className="mt-6 flex items-start gap-3 rounded-2xl bg-amber-50 border border-amber-100 p-4">
                  <ShieldCheck
                    size={19}
                    className="text-amber-600 flex-shrink-0 mt-0.5"
                  />

                  <div>
                    <p className="text-sm font-semibold text-amber-900">
                      Sign in required
                    </p>

                    <p className="text-xs text-amber-700 mt-1">
                      You need a Nexindi account to submit this form.
                    </p>

                    <Link
                      to="/signin"
                      className="inline-flex items-center gap-2 text-xs font-semibold text-blue-600 mt-3"
                    >
                      Sign In
                      <ArrowRight size={13} />
                    </Link>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={isSending}
                className="group w-full mt-7 inline-flex items-center justify-center gap-3 bg-blue-600 text-white px-7 py-4 rounded-xl font-semibold hover:bg-blue-700 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-blue-600/20 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSending ? (
                  <>
                    <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Sending Message...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}